import { Controller } from "@hotwired/stimulus"

// メニューアイテム名更新用のStimulusコントローラー
// 管理画面でメニューアイテムの名前を店舗でも変更できるようにする
export default class extends Controller {
  static targets = ["input", "submitButton", "message"]
  
  declare readonly inputTarget: HTMLInputElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly hasMessageTarget: boolean
  declare readonly messageTarget: HTMLElement
  
  // データ属性からメニューIDとメニューアイテムIDを取得
  static values = {
    menuId: String,
    menuItemId: String,
    updateUrl: String
  }
  
  declare readonly menuIdValue: string
  declare readonly menuItemIdValue: string
  declare readonly updateUrlValue: string
  
  // 初期値を保持（変更検知用）
  private originalValue: string = ""
  
  connect() {
    // 初期値を保持
    this.originalValue = this.inputTarget.value
    
    // 既存のcheck-change.jsと連携するため、
    // submitButtonにもdata-check-changeを動的に設定
    this.setupChangeDetection()
  }
  
  // 入力フィールドの変更検知を設定
  private setupChangeDetection() {
    // check-change.jsは初回変更時にリスナーを削除するため、
    // 更新後の再変更検知はこのコントローラーで行う
    this.inputTarget.addEventListener('input', () => {
      this.checkChanges()
    })
  }
  
  // 変更があるかチェック
  private checkChanges() {
    const isChanged = this.inputTarget.value !== this.originalValue
    
    if (isChanged) {
      // 値が変更された場合はボタンを有効化状態にする
      this.submitButtonTarget.classList.remove('uk-button-muted')
      this.submitButtonTarget.classList.add('uk-button-secondary')
    } else {
      // 元の値に戻った場合はボタンを無効化状態に戻す
      this.submitButtonTarget.classList.remove('uk-button-secondary')
      this.submitButtonTarget.classList.add('uk-button-muted')
    }
  }
  
  // 変更を反映ボタンがクリックされたときの処理
  async submitChange(event: Event) {
    event.preventDefault()
    
    const newName = this.inputTarget.value.trim()
    
    // 空の名前は許可しない
    if (!newName) {
      this.showMessage('メニューアイテム名を入力してください', 'danger')
      this.inputTarget.value = this.originalValue
      this.checkChanges()
      return
    }
    
    // 変更がない場合は何もしない
    if (newName === this.originalValue) {
      return
    }
    
    await this.updateMenuItemName(newName)
  }
  
  // メニューアイテム名をサーバーに送信して更新
  private async updateMenuItemName(newName: string) {
    // 送信中はボタンを無効化
    this.submitButtonTarget.disabled = true
    
    try {
      // CSRFトークンを取得
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: newName
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // 成功時の処理
        this.originalValue = newName
        
        // ボタンスタイルをリセット
        this.submitButtonTarget.classList.remove('uk-button-secondary')
        this.submitButtonTarget.classList.add('uk-button-muted')
        
        // プレビューモーダル内の対応する要素も更新
        this.updatePreviewElements(newName)
        
        // 成功メッセージを表示
        this.showMessage(data.message || 'メニューアイテム名を更新しました', 'success')
      } else {
        // エラー時の処理
        console.error('Update failed:', data.errors)
        
        // エラーメッセージを表示
        const errorMessage = data.errors?.join(', ') || 'エラーが発生しました'
        this.showMessage(errorMessage, 'danger')
        
        // 値を元に戻す
        this.inputTarget.value = this.originalValue
        this.checkChanges()
      }
    } catch (error) {
      console.error('Network error:', error)
      
      // ネットワークエラーメッセージを表示
      this.showMessage('通信エラーが発生しました', 'danger')
      
      // 値を元に戻す
      this.inputTarget.value = this.originalValue
      this.checkChanges()
    } finally {
      // ボタンを再度有効化
      this.submitButtonTarget.disabled = false
    }
  }
  
  // プレビューモーダル内の関連要素を更新
  private updatePreviewElements(newName: string) {
    const menuItemId = this.menuItemIdValue
    
    // メニュープレビュー内の要素を更新
    const previewElement = document.querySelector(`[data-preview-menu-item-id="${menuItemId}"]`)
    if (previewElement) {
      previewElement.textContent = newName
    }
    
    // レシート作成セクションのメニュー一覧内の要素を更新
    const receiptElement = document.querySelector(`[data-receipt-menu-item-id="${menuItemId}"]`)
    if (receiptElement) {
      receiptElement.textContent = newName
    }
    
    // レシートプレビュー内の要素も更新（もし存在すれば）
    const receiptPreviewElement = document.querySelector(`[data-receipt-preview-menu-item-id="${menuItemId}"]`)
    if (receiptPreviewElement) {
      receiptPreviewElement.textContent = newName
    }
  }
  
  // メッセージを表示（UIKit notification使用）
  private showMessage(message: string, status: 'success' | 'danger') {
    if (typeof window.UIkit !== 'undefined') {
      window.UIkit.notification({
        message: message,
        status: status,
        pos: 'top-right',
        timeout: 3000
      })
    }
  }
}